import { Platform } from 'react-native';
import Sound from 'react-native-sound';
import { getActiveResolvedVisualThemeId } from '../theme/activeVisualThemeRef';
import { RING_FADE_IN_MS, SOUND_VOLUME, areSoundsEnabled, applyAudioCategory, stopAndReleaseNativeSound } from './appSounds';
import {
  ringSoundAndroidRes,
  ringSoundIosFilename,
  ringSoundWebAsset,
} from '../theme/themeSounds';

/** Один экземпляр; не открываем микрофон/WebRTC до «Ответить». */
let webRing: HTMLAudioElement | null = null;
let nativeRing: Sound | null = null;
let ringPlayInFlight: Promise<void> | null = null;
let fadeTimer: ReturnType<typeof setInterval> | null = null;

/** Шаг нарастания громкости, мс. 60 мс — на слух непрерывно, но это ~23 вызова, а не 1400. */
const FADE_STEP_MS = 60;

function stopFade(): void {
  if (fadeTimer !== null) {
    clearInterval(fadeTimer);
    fadeTimer = null;
  }
}

/**
 * Плавно поднять громкость от четверти до полной за {@link RING_FADE_IN_MS}.
 *
 * <p>Так звонят iOS и Pixel, и дело не в красоте: телефон, лежащий рядом, не должен бить
 * с первой ноты, а телефон в сумке всё равно дозвонится со второго цикла. Прошлый рингтон
 * стартовал сразу на 1.0.
 *
 * <p>Рампа по громкости, а не фейд внутри файла: файл зациклен, и запечённое нарастание
 * повторялось бы каждые четыре секунды — тихо становилось бы не в начале звонка, а
 * в начале каждого круга.
 */
function rampVolume(apply: (v: number) => void): void {
  stopFade();
  const target = SOUND_VOLUME.ring;
  const start = target * 0.25;
  let elapsed = 0;
  apply(start);
  fadeTimer = setInterval(() => {
    elapsed += FADE_STEP_MS;
    const k = Math.min(1, elapsed / RING_FADE_IN_MS);
    apply(start + (target - start) * k);
    if (k >= 1) stopFade();
  }, FADE_STEP_MS);
}

/** Остановить входящий ринг (ответ, сброс, bye, уход с экрана). */
export async function stopIncomingCallRingtone(): Promise<void> {
  stopFade();
  try {
    if (Platform.OS === 'web') {
      if (webRing) {
        webRing.loop = false;
        webRing.pause();
        try {
          webRing.currentTime = 0;
        } catch {
          /* noop */
        }
        webRing.removeAttribute('src');
        webRing.load();
        webRing = null;
      }
      return;
    }
    const s = nativeRing;
    nativeRing = null;
    if (s) {
      await stopAndReleaseNativeSound(s);
    }
  } catch {
    /* noop */
  }
}

function createNativeRing(
  theme: ReturnType<typeof getActiveResolvedVisualThemeId>,
  onError: (err: unknown) => void,
  onReady: (s: Sound) => void,
): void {
  const androidRes = ringSoundAndroidRes(theme);
  const iosFile = ringSoundIosFilename(theme);
  if (Platform.OS === 'android') {
    const s = new Sound(androidRes, '', (err) => {
      if (err) {
        onError(err);
        return;
      }
      onReady(s);
    });
    return;
  }
  const s = new Sound(iosFile, Sound.MAIN_BUNDLE, (err) => {
    if (err) {
      onError(err);
      return;
    }
    onReady(s);
  });
}

/**
 * Входящий звонок: один зацикленный проигрыватель.
 * Мелодия зависит от выбранного визуального стиля.
 */
export async function playIncomingCallRingtone(): Promise<void> {
  while (ringPlayInFlight) {
    await ringPlayInFlight;
  }

  const run = (async () => {
    try {
      /** Переключатель «Звуки» из настроек. Экран входящего остаётся, звонок — беззвучный. */
      if (!areSoundsEnabled()) return;
      const theme = getActiveResolvedVisualThemeId();
      if (Platform.OS === 'web') {
        const uri = ringSoundWebAsset(theme) || '';
        if (!uri) return;
        if (webRing) {
          try {
            if (!webRing.paused && !webRing.ended) return;
          } catch {
            /* noop */
          }
          webRing.pause();
          webRing.src = '';
          webRing = null;
        }
        const HtmlAudio = globalThis.Audio;
        const audio = new HtmlAudio(uri);
        audio.loop = true;
        audio.preload = 'auto';
        try {
          audio.setAttribute('playsinline', 'true');
          audio.setAttribute('webkit-playsinline', 'true');
        } catch {
          /* noop */
        }
        webRing = audio;
        rampVolume((v) => {
          try {
            audio.volume = v;
          } catch {
            /* элемент могли уже снести */
          }
        });
        void audio.play().catch(() => {});
        return;
      }

      /**
       * Входящий звонок идёт по тому же маршруту, что и системный рингтон: на Android это
       * `STREAM_RING`, на iOS — `SoloAmbient`. Стоявший здесь `Playback` беззвучный режим
       * телефона игнорировал на обеих платформах, и приложение звонило в кинотеатре.
       * Категория читается при `new Sound(...)`, поэтому выставляется до него.
       */
      applyAudioCategory('ring');

      if (nativeRing) {
        await stopIncomingCallRingtone();
      }

      await new Promise<void>((resolve, reject) => {
        createNativeRing(
          theme,
          (err) => reject(err),
          (s) => {
            s.setNumberOfLoops(-1);
            nativeRing = s;
            rampVolume((v) => {
              // Проигрыватель мог быть остановлен и освобождён между тиками рампы.
              if (nativeRing !== s) return;
              try {
                s.setVolume(v);
              } catch {
                /* noop */
              }
            });
            s.play();
            resolve();
          },
        );
      });
    } catch {
      /* нет динамика */
    }
  })();

  ringPlayInFlight = run.finally(() => {
    ringPlayInFlight = null;
  });
  await ringPlayInFlight;
}
